import React, { useCallback, useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { sportInfo, formatMatchTime, formatRupiah, normalizeWa } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import PageShell from "../components/PageShell";
import { ArrowLeft, MapPin, Clock, Coins, Users, MessageCircle, UserX, ExternalLink, Send, X, Crown, Check, Trash2, Share2, MessageSquare } from "lucide-react";
import ShareSheet from "../components/ShareSheet";
import PaymentRecap from "../components/PaymentRecap";
import PlayerProfileModal from "../components/PlayerProfileModal";

export default function MatchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [profileViewing, setProfileViewing] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/matches/${id}`);
      setData(data);
    } catch (err) {
      console.warn("[match-detail] load failed:", err?.message);
      toast.error("Match tidak ditemukan");
      navigate("/home");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleJoin = async () => {
    setJoining(true);
    try {
      const { data: res } = await api.post(`/matches/${id}/join`);
      toast.success(res.status === "waitlisted" ? "Masuk waitlist!" : "Berhasil join!");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal join");
    } finally {
      setJoining(false);
    }
  };

  const handleLeave = async () => {
    if (!window.confirm("Yakin keluar dari match ini?")) return;
    try {
      await api.post(`/matches/${id}/leave`);
      toast.success("Sudah keluar");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal");
    }
  };

  const handleKick = async (uid) => {
    if (!window.confirm("Kick player ini?")) return;
    try {
      await api.post(`/matches/${id}/kick`, { user_id: uid });
      toast.success("Player dikeluarkan");
      await load();
    } catch (e) {
      toast.error("Gagal kick");
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Batalkan match ini? Tidak bisa di-undo.")) return;
    try {
      await api.delete(`/matches/${id}`);
      toast.success("Match dibatalkan");
      navigate("/home");
    } catch (e) {
      toast.error("Gagal batalkan");
    }
  };

  if (loading || !data) {
    return (
      <PageShell hasBottomNav={false}>
        <div className="px-4 py-10"><div className="h-40 rounded-2xl bg-card animate-pulse" /></div>
      </PageShell>
    );
  }

  const m = data.match;
  const sport = sportInfo(m.sport);
  const playerCount = m.player_ids.length;
  const fillPct = Math.min(100, (playerCount / m.max_players) * 100);
  const isOpen = m.status === "OPEN";
  const isFull = m.status === "FULL";
  const isFinished = m.status === "FINISHED" || m.status === "CANCELLED";

  return (
    <PageShell hasBottomNav={false}>
      {/* Hero */}
      <div className="relative px-4 pt-6 pb-4">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)} data-testid="back-btn" className="h-10 w-10 rounded-full bg-secondary border border-border flex items-center justify-center">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowShare(true)}
              data-testid="share-btn"
              className="h-10 w-10 rounded-full bg-secondary border border-border flex items-center justify-center hover:border-primary/40"
              aria-label="Bagikan"
            >
              <Share2 className="h-5 w-5" />
            </button>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full ${isFinished ? "bg-secondary text-muted-foreground" : isFull ? "bg-emerald-400/10 text-emerald-400" : "bg-primary/10 text-primary"}`}>
              {m.status}
            </span>
          </div>
        </div>

        <div className="mt-6 relative">
          <div aria-hidden className="absolute -right-10 -top-10 h-44 w-44 rounded-full blur-3xl opacity-30" style={{ background: sport.color }} />
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-3xl flex items-center justify-center text-4xl" style={{ background: sport.color + "22", border: `1px solid ${sport.color}44` }}>
              {sport.icon}
            </div>
            <div>
              <h1 className="font-heading text-3xl font-black tracking-tight">{sport.label}</h1>
              <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" /> {m.venue}
              </div>
            </div>
          </div>
          {data.team && (
            <Link
              to={`/teams/${data.team.team_id}`}
              data-testid="match-team-pill"
              className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20 transition-colors"
            >
              <div className="h-5 w-5 rounded-full overflow-hidden bg-card flex items-center justify-center">
                {data.team.logo ? <img src={data.team.logo} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px]">🏆</span>}
              </div>
              <span className="text-xs font-bold">Hosted by {data.team.name}</span>
            </Link>
          )}
        </div>

        {/* Slot bar */}
        <div className="mt-6 bg-card border border-border rounded-2xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Slot</div>
              <div className="font-heading text-3xl font-black mt-1">
                <span className="text-primary">{playerCount}</span><span className="text-muted-foreground">/{m.max_players}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Skill</div>
              <div className="text-base font-bold mt-1 capitalize">{m.skill_level === "all" ? "Semua" : m.skill_level}</div>
            </div>
          </div>
          <div className="mt-3 h-2 bg-secondary rounded-full overflow-hidden">
            <div className="h-full rounded-full slot-fill-anim" style={{ width: `${fillPct}%`, background: isFull ? "#10B981" : "#00E5FF" }} />
          </div>
        </div>

        {/* Info */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <InfoCard icon={Clock} label="Waktu" value={formatMatchTime(m.starts_at)} />
          <InfoCard icon={Coins} label="Biaya" value={formatRupiah(m.cost_per_player)} />
          <InfoCard icon={MapPin} label="Kota" value={m.city} />
          <InfoCard icon={Users} label="Durasi" value={`${m.duration_minutes} menit`} />
        </div>

        {m.description && (
          <div className="mt-4 bg-card border border-border rounded-2xl p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Deskripsi</div>
            <div className="text-sm whitespace-pre-wrap">{m.description}</div>
          </div>
        )}

        {/* Host */}
        <div className="mt-4 bg-card border border-border rounded-2xl p-4 flex items-center gap-3">
          <Avatar user={data.host} />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Crown className="h-3 w-3 text-primary" /> Host</div>
            <div className="font-bold truncate">{data.host?.name || "Host"}</div>
            <div className="text-xs text-muted-foreground">Reliability {data.host?.reliability_score?.toFixed(0) ?? 100}%</div>
          </div>
        </div>

        {/* Players list */}
        <div className="mt-4">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2 px-1">Pemain ({playerCount}/{m.max_players})</div>
          <div className="space-y-2">
            {data.players.map((p) => (
              <div key={p.user_id} className="bg-card border border-border rounded-2xl p-3 flex items-center gap-3" data-testid={`player-row-${p.user_id}`}>
                <button
                  type="button"
                  onClick={() => setProfileViewing(p)}
                  data-testid={`player-open-${p.user_id}`}
                  className="flex items-center gap-3 flex-1 min-w-0 text-left active:scale-[0.99] transition-transform"
                >
                  <Avatar user={p} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate flex items-center gap-1">
                      {p.name}
                      {p.user_id === m.host_id && <Crown className="h-3.5 w-3.5 text-primary" />}
                    </div>
                    <div className="text-xs text-muted-foreground">{p.city || ""}</div>
                  </div>
                </button>
                {p.whatsapp && p.user_id !== user?.user_id && (
                  <a
                    href={`https://wa.me/${normalizeWa(p.whatsapp)}?text=${encodeURIComponent(`Halo ${p.name}, soal match ${m.venue} nih`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`wa-${p.user_id}`}
                    className="h-8 w-8 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 hover:bg-emerald-500/20"
                    aria-label={`Chat WhatsApp ${p.name}`}
                  >
                    <MessageSquare className="h-4 w-4" />
                  </a>
                )}
                {data.is_host && p.user_id !== m.host_id && !isFinished && (
                  <button data-testid={`kick-${p.user_id}`} onClick={() => handleKick(p.user_id)} className="h-8 w-8 rounded-full bg-secondary hover:bg-destructive/20 flex items-center justify-center text-muted-foreground hover:text-destructive">
                    <UserX className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            {data.waitlist.length > 0 && (
              <>
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mt-4 mb-2 px-1">Waitlist ({data.waitlist.length})</div>
                {data.waitlist.map((p) => (
                  <div key={p.user_id} className="bg-card border border-border rounded-2xl p-3 flex items-center gap-3 opacity-70">
                    <Avatar user={p} />
                    <div className="flex-1"><div className="font-bold">{p.name}</div></div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {m.whatsapp_link && data.is_joined && (
          <a href={m.whatsapp_link} target="_blank" rel="noopener noreferrer" className="mt-4 bg-card border border-border rounded-2xl p-3 flex items-center gap-2 text-sm font-bold">
            <ExternalLink className="h-4 w-4" /> Buka link WhatsApp
          </a>
        )}

        {/* Payment Recap — only for joined players & host */}
        {(data.is_joined || data.is_host) && (
          <PaymentRecap
            match={m}
            players={data.players}
            host={data.host}
            isHost={data.is_host}
            currentUserId={user?.user_id}
            onUpdated={load}
          />
        )}
      </div>

      {/* Floating action bar */}
      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md z-30 px-4 pt-3 bg-gradient-to-t from-[#09090B] via-[#09090B]/95 to-transparent" style={{ paddingBottom: "max(72px, calc(env(safe-area-inset-bottom) + 72px))" }}>
        {isFinished ? (
          <div className="w-full px-6 py-4 rounded-full bg-secondary text-center font-bold text-muted-foreground">
            {m.status === "CANCELLED" ? "Match Dibatalkan" : "Match Selesai"}
          </div>
        ) : data.is_joined ? (
          <div className="flex gap-2">
            <button
              data-testid="open-chat-btn"
              onClick={() => setShowChat(true)}
              className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-primary text-primary-foreground font-bold glow-cyan active:scale-95"
            >
              <MessageCircle className="h-4 w-4" /> Chat
            </button>
            {data.is_host ? (
              <button data-testid="cancel-match-btn" onClick={handleCancel} className="px-5 py-4 rounded-full bg-destructive/15 text-destructive font-bold border border-destructive/30">
                <Trash2 className="h-4 w-4" />
              </button>
            ) : (
              <button data-testid="leave-btn" onClick={handleLeave} className="px-5 py-4 rounded-full bg-secondary text-foreground font-bold border border-border">
                Keluar
              </button>
            )}
          </div>
        ) : (
          <button
            data-testid="join-btn"
            onClick={handleJoin}
            disabled={joining}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-primary text-primary-foreground font-bold glow-cyan-strong active:scale-95 disabled:opacity-50"
          >
            {joining ? "Loading..." : isFull ? "Masuk Waitlist" : data.is_waitlisted ? "Sudah di Waitlist" : "Join Match"}
          </button>
        )}
      </div>

      {showChat && <ChatPanel matchId={id} onClose={() => setShowChat(false)} user={user} />}
      {showShare && <ShareSheet match={m} host={data.host} onClose={() => setShowShare(false)} />}
      {profileViewing && (
        <PlayerProfileModal
          player={profileViewing}
          isHost={profileViewing.user_id === m.host_id}
          onClose={() => setProfileViewing(null)}
        />
      )}
    </PageShell>
  );
}

function InfoCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-3">
      <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="font-bold text-sm mt-1">{value}</div>
    </div>
  );
}

function Avatar({ user }) {
  const letter = (user?.name || "?")[0]?.toUpperCase();
  return (
    <div className="h-10 w-10 rounded-full bg-secondary border border-border flex items-center justify-center overflow-hidden shrink-0">
      {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : <span className="font-bold text-sm">{letter}</span>}
    </div>
  );
}

function ChatPanel({ matchId, onClose, user }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const lastTs = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const params = lastTs.current ? { since: lastTs.current } : {};
      const { data } = await api.get(`/matches/${matchId}/messages`, { params });
      if (data.length > 0) {
        setMessages((prev) => [...prev, ...data]);
        lastTs.current = data[data.length - 1].created_at;
      }
    } catch (err) {
      console.warn("[chat] fetchMessages failed:", err?.message);
    }
  }, [matchId]);

  useEffect(() => {
    fetchMessages();
    const t = setInterval(fetchMessages, 4000);
    return () => clearInterval(t);
  }, [fetchMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const send = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/matches/${matchId}/messages`, { text: input.trim() });
      setInput("");
      await fetchMessages();
    } catch (err) {
      console.warn("[chat] send failed:", err?.message);
      toast.error("Gagal kirim");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-md h-[85vh] sm:h-[600px] bg-card border border-border rounded-t-3xl sm:rounded-3xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="chat-panel"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div>
            <div className="font-heading font-bold">Match Chat</div>
            <div className="text-xs text-muted-foreground">Auto-archived setelah match</div>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {messages.length === 0 && (
            <div className="text-center text-sm text-muted-foreground py-10">Belum ada pesan. Mulai chat!</div>
          )}
          {messages.map((m) => {
            const isMe = m.user_id === user?.user_id;
            return (
              <div key={m.message_id} className={`flex gap-2 ${isMe ? "justify-end" : "justify-start"}`}>
                {!isMe && <Avatar user={{ name: m.user_name, picture: m.user_picture }} />}
                <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 ${isMe ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
                  {!isMe && <div className="text-[10px] font-bold opacity-70 mb-0.5">{m.user_name}</div>}
                  <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
        <div className="border-t border-border p-3 flex gap-2">
          <input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ketik pesan..."
            className="flex-1 bg-secondary border border-border rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-primary"
          />
          <button
            data-testid="chat-send"
            onClick={send}
            disabled={!input.trim() || sending}
            className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-40"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
